import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    ButtonGroup,
    Button,
    IconButton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {HeartIcon, HeartSolidIcon} from '../../icons'
import withRegistration from '../../with-registration'

const ButtonWithRegistrationForWishlist = withRegistration(Button, {isWishlistAction: true})
const IconButtonWithRegistrationForWishlist = withRegistration(IconButton, {isWishlistAction: true})

const ActionButtons = ({
    shouldShowShopSimilar,
    shouldShowNotifyMe,
    shouldShowAddToCart,
    shouldShowEarlyAccess,
    ctaLoading,
    isNotifyMeLoading,
    disableButton,
    addToCart,
    updateCart,
    addToWishlist,
    updateWishlist,
    isPDP,
    isProductPartOfBundle,
    isGiftPage,
    isProductASet,
    isProductABundle,
    canAddToWishlist,
    isWishlistLoading,
    isInWishlist,
    onCartItem,
    onNotifyMe,
    onShopSimilar,
    onWishlistItem,
    onEarlyAccess,
    wishlistProductId,
    buttonText,
    notifyMeButtonText,
    notifyMeButtonSx
}) => {
    const styles = useMultiStyleConfig('ProductView', {isPDP})
    const shouldRenderEarlyAccess =
        shouldShowEarlyAccess && !shouldShowShopSimilar && !shouldShowNotifyMe && !shouldShowAddToCart

    if ((addToCart || updateCart) && !isProductPartOfBundle) {
        return (
            <ButtonGroup
                key="cart-wishlist-group"
                size="md"
                isAttached
                variant="solid"
                {...styles.buttonGroup}
            >
                {shouldRenderEarlyAccess && (
                    <Button
                        data-testid="pdp-early-access-button"
                        {...styles.earlyAccessButton}
                        onClick={onEarlyAccess}
                        isLoading={ctaLoading}
                        _focusVisible={{
                            outline: '2px solid',
                            outlineColor: 'webTheme.focus',
                            outlineOffset: '2px'
                        }}
                    >
                        <FormattedMessage
                            defaultMessage="Create Account/Log In"
                            id="pdp_early_access.cta.label"
                        />
                    </Button>
                )}

                {shouldShowShopSimilar && (
                    <Button
                        data-testid="pdp-shop-similar-button"
                        {...styles.shopSimilarButton}
                        onClick={onShopSimilar}
                        isLoading={ctaLoading}
                        _focusVisible={{
                            outline: '2px solid',
                            outlineColor: 'webTheme.focus',
                            outlineOffset: '2px'
                        }}
                    >
                        <FormattedMessage
                            defaultMessage="Shop similar"
                            id="pdp_shop_similar.cta.label"
                        />
                    </Button>
                )}

                {shouldShowNotifyMe && (
                    <Button
                        key="notify-me-button"
                        data-testid="pdp-notify-me-button"
                        onClick={onNotifyMe}
                        isLoading={ctaLoading || isNotifyMeLoading}
                        {...styles.notifyMeButton}
                        sx={notifyMeButtonSx || undefined}
                        _focusVisible={{
                            outline: '2px solid',
                            outlineColor: 'webTheme.focus',
                            outlineOffset: '2px'
                        }}
                    >
                        {notifyMeButtonText ? (
                            notifyMeButtonText
                        ) : (
                            <FormattedMessage
                                defaultMessage="Notify Me"
                                id="pdp_notify_me_cta.label"
                            />
                        )}
                    </Button>
                )}

                {shouldShowAddToCart && (
                    <Button
                        {...styles.addToCartButton}
                        variant="webPrimary"
                        onClick={onCartItem}
                        isDisabled={disableButton}
                        isLoading={ctaLoading}
                        _focusVisible={{
                            outline: '2px solid',
                            outlineColor: 'webTheme.focus',
                            outlineOffset: '2px'
                        }}
                        aria-label={
                            updateCart
                                ? buttonText.update
                                : isProductASet
                                  ? buttonText.addSetToCart
                                  : isProductABundle
                                    ? buttonText.addBundleToCart
                                    : buttonText.addToCart
                        }
                        data-testid={`add-to-bag${disableButton ? '-disabled' : ''}`}
                    >
                        {updateCart
                            ? buttonText.update
                            : isProductASet
                              ? buttonText.addSetToCart
                              : isProductABundle
                                ? buttonText.addBundleToCart
                                : buttonText.addToCart}
                    </Button>
                )}

                {(addToWishlist || updateWishlist) && canAddToWishlist && !isGiftPage && (
                    <IconButtonWithRegistrationForWishlist
                        wishlistProductId={wishlistProductId}
                        wishlistClickPage={isPDP ? 'pdp' : 'plp'}
                        data-testid="pdp-wishlist-button"
                        variant="outline"
                        onClick={onWishlistItem}
                        isDisabled={isWishlistLoading || !canAddToWishlist}
                        isLoading={isWishlistLoading}
                        aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
                        icon={isInWishlist ? <HeartSolidIcon /> : <HeartIcon />}
                        {...styles.wishlistIconButton}
                    />
                )}
            </ButtonGroup>
        )
    }

    if ((addToWishlist || updateWishlist) && !isProductPartOfBundle && !isGiftPage) {
        return (
            <ButtonWithRegistrationForWishlist
                wishlistProductId={wishlistProductId}
                wishlistClickPage={isPDP ? 'pdp' : 'plp'}
                key="wishlist-only-button"
                data-testid="pdp-wishlist-button"
                onClick={onWishlistItem}
                disabled={isWishlistLoading || !canAddToWishlist}
                isLoading={isWishlistLoading}
                {...styles.wishlistFullButton}
                leftIcon={isInWishlist ? <HeartSolidIcon /> : <HeartIcon />}
            >
                {updateWishlist ? buttonText.update : buttonText.addToWishlist}
            </ButtonWithRegistrationForWishlist>
        )
    }

    return null
}

ActionButtons.propTypes = {
    shouldShowShopSimilar: PropTypes.bool,
    shouldShowNotifyMe: PropTypes.bool,
    shouldShowAddToCart: PropTypes.bool,
    ctaLoading: PropTypes.bool,
    isNotifyMeLoading: PropTypes.bool,
    disableButton: PropTypes.bool,
    addToCart: PropTypes.func,
    updateCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    updateWishlist: PropTypes.func,
    isProductPartOfBundle: PropTypes.bool,
    isGiftPage: PropTypes.bool,
    isProductASet: PropTypes.bool,
    isProductABundle: PropTypes.bool,
    canAddToWishlist: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    isInWishlist: PropTypes.bool,
    onCartItem: PropTypes.func,
    onNotifyMe: PropTypes.func,
    onShopSimilar: PropTypes.func,
    onEarlyAccess: PropTypes.func,
    onWishlistItem: PropTypes.func,
    wishlistProductId: PropTypes.string,
    buttonText: PropTypes.shape({
        update: PropTypes.string,
        addToCart: PropTypes.string,
        addSetToCart: PropTypes.string,
        addBundleToCart: PropTypes.string,
        addToWishlist: PropTypes.string
    }),
    notifyMeButtonText: PropTypes.string,
    notifyMeButtonSx: PropTypes.object
}

export default ActionButtons
